/*
 * <zpen-before-after>
 * Before / after slider. A hidden range input drives the --ba-pos custom property (drag, touch, arrow keys).
 * When the slider first comes into view it slides across once to show that it can be moved.
 */
(() => {
  if (customElements.get('zpen-before-after')) return;

  class ZpenBeforeAfter extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.range = this.querySelector('.zba__range');
      if (!this.range) return;

      this.start = Number(this.range.value) || 50;
      this.raf = 0;
      this.set(this.start);

      this.onInput = () => {
        this.stopTeaser();
        this.set(Number(this.range.value));
      };
      this.range.addEventListener('input', this.onInput); // keyboard

      // dragging with mouse, pen or finger (vertical swipes still scroll the page)
      this.dragging = false;
      const move = (event) => {
        const rect = this.getBoundingClientRect();
        this.set(((event.clientX - rect.left) / rect.width) * 100);
      };
      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.stopTeaser();
        this.dragging = true;
        this.setPointerCapture(event.pointerId);
        move(event);
      });
      this.addEventListener('pointermove', (event) => {
        if (this.dragging) move(event);
      });
      const end = (event) => {
        if (!this.dragging) return;
        this.dragging = false;
        if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
      };
      this.addEventListener('pointerup', end);
      this.addEventListener('pointercancel', end);

      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            setTimeout(() => this.teaser(), 400);
          },
          { threshold: 0.6 }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      this.ready = false;
      this.stopTeaser();
      this.observer?.disconnect();
    }

    set(value) {
      const v = Math.max(0, Math.min(100, value));
      this.style.setProperty('--ba-pos', v + '%');
      this.range.value = v;
      this.range.setAttribute('aria-valuetext', Math.round(v) + '%');
    }

    // slide to the right, then to the left, then back to the start (once, cancelled by any interaction)
    teaser() {
      const path = [this.start, 88, 12, this.start];
      const durations = [900, 1500, 900];
      let leg = 0;
      let from = performance.now();
      const ease = (t) => t * t * (3 - 2 * t);
      const tick = (now) => {
        const t = Math.min(1, (now - from) / durations[leg]);
        this.set(path[leg] + (path[leg + 1] - path[leg]) * ease(t));
        if (t < 1) {
          this.raf = requestAnimationFrame(tick);
        } else if (++leg < durations.length) {
          from = now;
          this.raf = requestAnimationFrame(tick);
        } else {
          this.raf = 0;
        }
      };
      this.raf = requestAnimationFrame(tick);
    }

    stopTeaser() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.observer?.disconnect();
    }
  }

  customElements.define('zpen-before-after', ZpenBeforeAfter);
})();
